import SubdirectoryArrowRight from "@assets/images/SubdirectoryArrowRight.svg";
import SubdirectoryArrowUpRight from "@assets/images/SubdirectoryArrowUpRight.svg";
import truckIcon from "@assets/images/truck.svg";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from "@material-ui/core";
import DialogProcessing from "@saleor/components/DialogProcessing";
import MyTypography from "@saleor/components/MyTypography";
import StickyHorizontalScrollBar from "@saleor/components/StickyHorizontalScrollBar";
import TableCellHeader from "@saleor/components/TableCellHeader";
import { BIG_ROW_NUMBER_SET } from "@saleor/components/TablePagination/constants";
import { TablePaginationCustom } from "@saleor/components/TablePaginationCustom";
import ModalDp from "@saleor/deliveryReport/components/ModalDp";
import ModalGpsTracking from "@saleor/deliveryReport/components/ModalGpsTracking";
import { DeliveryReportSortField } from "@saleor/deliveryReport/urls";
import { DataItemCommon } from "@saleor/deliveryReport/views/utils/sort";
import {
  GetLmsReportCsCustomerQuery,
  useGetgpsReportCsCustomerLazyQuery
} from "@saleor/graphql";
import useNotifier from "@saleor/hooks/useNotifier";
import { renderCollection } from "@saleor/misc";
import { ListProps, SortPage } from "@saleor/types";
import { getArrowDirection } from "@saleor/utils/sort";
import classNames from "classnames";
import moment from "moment-timezone/moment-timezone-utils";
import React, { useEffect, useMemo, useRef, useState } from "react";
import SVG from "react-inlinesvg";
import { FormattedNumber, useIntl } from "react-intl";

import { ModalDpDetail } from "../../types";
import { useStyles } from "./styles";

export interface ITableProps
  extends ListProps,
    SortPage<DeliveryReportSortField> {
  fetchData: () => void;
  onPreviousFirstPage: () => void;
  onNextLastPage: () => void;
  disabled: boolean;
  latestPageItemNumber: number | null;
  onNextPage: () => void;
  onPreviousPage: () => void;
  totalCount: number;
  currentTab?: string;
  orderTrackingOutputList: GetLmsReportCsCustomerQuery["getLmsReportCsCustomer"];
  currentData: DataItemCommon[];
}

const DeliveryReportTableCustomer: React.FC<ITableProps> = props => {
  const {
    onPreviousFirstPage,
    onNextLastPage,
    disabled,
    latestPageItemNumber,
    onNextPage,
    onPreviousPage,
    totalCount,
    pageInfo,
    settings,
    sort,
    onSort,
    onUpdateListSettings,
    orderTrackingOutputList,
    currentData
  } = props;

  const intl = useIntl();
  const classes = useStyles();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [openGpsTracking, setOpenGpsTracking] = useState<boolean>(false);
  const [dataItem, setDataItem] = useState<DataItemCommon>(null);
  const [openDp, setOpenDp] = useState<boolean>(false);
  const [dataItemDpModal, setDataItemDpModal] = useState<DataItemCommon[]>([]);
  const notify = useNotifier();
  const [modalDpDetail, setModalDpDetail] = useState<ModalDpDetail>(null);
  const orderTrackingOutput = useMemo(() => {
    if (dataItem) {
      return orderTrackingOutputList.find(
        itemTracking => itemTracking.dnNo === dataItem.dnNo
      );
    }
    return null;
  }, [dataItem]);

  const [
    fetchData,
    { data, loading: loadingGpsTracking, error }
  ] = useGetgpsReportCsCustomerLazyQuery({
    fetchPolicy: "no-cache"
  });
  const [responseItem, setResponseItem] = useState({});
  useEffect(() => {
    if (error) {
      notify({
        status: "error",
        apiMessage: error?.message
      });
    } else if (
      data &&
      data.getGpsReportCsCustomer &&
      data.getGpsReportCsCustomer.length > 0
    ) {
      setResponseItem(data.getGpsReportCsCustomer[0]);
      setOpenGpsTracking(true);
    }
  }, [data, error]);
  const handleGpsTracking = (item: DataItemCommon) => {
    fetchData({
      variables: {
        filter: {
          dp: item.billNo || item.dnNo,
          shippingPoint: item?.shippingPoint ? item?.shippingPoint : "",
          soldTo: item.soldToCode
        }
      }
    });
    setDataItem(item);
  };

  const handleDpLink = (item: DataItemCommon, type: string) => {
    if (!item.billNo) {
      return;
    }
    setDataItem(item);
    const modalDpInfo = {
      displayType: type,
      titleModal: `Billing No.: ${item.billNo || "-"}`
    };
    const dataItemFilter = currentData.filter(e => e.billNo === item.billNo);
    setDataItemDpModal(dataItemFilter);
    setOpenDp(true);
    setModalDpDetail(modalDpInfo);
  };

  const matNumberFilter = (item: DataItemCommon) => {
    if (
      item?.deliveryGrp &&
      item?.parentItemNo &&
      item?.deliveryGrp !== "000" &&
      item?.parentItemNo !== "000000"
    ) {
      const isArrowDirection = getArrowDirection(sort.asc);
      const arrowIcon =
        isArrowDirection === "asc"
          ? SubdirectoryArrowRight
          : SubdirectoryArrowUpRight;
      return (
        <div className={classes.flex}>
          <SVG
            src={arrowIcon}
            width={25}
            height={25}
            className={classes.marginRightHeader}
          />
          <span>{item.matNumber}</span>
        </div>
      );
    }

    return item.matNumber || "-";
  };

  return (
    <>
      {currentData.length > 0 ? (
        <>
          <MyTypography className={classes.countTitle} variant={"h7"}>
            {intl.formatMessage(
              { defaultMessage: "รายการที่เกี่ยวข้อง ({count})" },
              { count: totalCount }
            )}
          </MyTypography>
          <div className={classes.root} ref={containerRef}>
            <TableContainer className={classNames(classes.tableContainer)}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCellHeader
                      className={classNames(classes.col1, classes.freeze1)}
                    >
                      {intl.formatMessage({ defaultMessage: "GPS Tracking" })}
                    </TableCellHeader>
                    <TableCellHeader
                      className={classNames(classes.col2, classes.freeze2)}
                      direction={
                        sort.sort === DeliveryReportSortField.dnNo
                          ? getArrowDirection(sort.asc)
                          : undefined
                      }
                      arrowPosition="right"
                      onClick={() => onSort(DeliveryReportSortField.dnNo)}
                    >
                      {intl.formatMessage({ defaultMessage: "เลขที่ใบจ่าย" })}
                    </TableCellHeader>
                    <TableCellHeader
                      className={classNames(classes.col20, classes.freeze3)}
                      direction={
                        sort.sort === DeliveryReportSortField.billNo
                          ? getArrowDirection(sort.asc)
                          : undefined
                      }
                      arrowPosition="right"
                      onClick={() => onSort(DeliveryReportSortField.billNo)}
                    >
                      {intl.formatMessage({ defaultMessage: "Billing No." })}
                    </TableCellHeader>
                    <TableCellHeader
                      className={classes.col3}
                      direction={
                        sort.sort === DeliveryReportSortField.poNo
                          ? getArrowDirection(sort.asc)
                          : undefined
                      }
                      arrowPosition="right"
                      onClick={() => onSort(DeliveryReportSortField.poNo)}
                    >
                      {intl.formatMessage({
                        defaultMessage: "เลขที่ใบสั่งซื้อ"
                      })}
                      <MyTypography variant={"body3"}>
                        {intl.formatMessage({ defaultMessage: "(P/O)" })}
                      </MyTypography>
                    </TableCellHeader>
                    <TableCellHeader
                      className={classes.col4}
                      direction={
                        sort.sort === DeliveryReportSortField.soNo
                          ? getArrowDirection(sort.asc)
                          : undefined
                      }
                      arrowPosition="right"
                      onClick={() => onSort(DeliveryReportSortField.soNo)}
                    >
                      {intl.formatMessage({ defaultMessage: "เลขที่ใบสั่ง" })}
                      <MyTypography variant={"body3"}>
                        {intl.formatMessage({ defaultMessage: "(S/O)" })}
                      </MyTypography>
                    </TableCellHeader>
                    <TableCellHeader
                      className={classes.col5}
                      textAlign={"center"}
                      direction={
                        sort.sort === DeliveryReportSortField.itemNo
                          ? getArrowDirection(sort.asc)
                          : undefined
                      }
                      arrowPosition="right"
                      onClick={() => onSort(DeliveryReportSortField.itemNo)}
                    >
                      {intl.formatMessage({ defaultMessage: "Item No." })}
                    </TableCellHeader>
                    <TableCellHeader
                      className={classes.col18}
                      textAlign={"center"}
                      direction={
                        sort.sort === DeliveryReportSortField.matNumber
                          ? getArrowDirection(sort.asc)
                          : undefined
                      }
                      arrowPosition="right"
                      onClick={() => onSort(DeliveryReportSortField.matNumber)}
                    >
                      {intl.formatMessage({ defaultMessage: "Material Code" })}
                    </TableCellHeader>
                    <TableCellHeader
                      className={classes.col6}
                      direction={
                        sort.sort ===
                        DeliveryReportSortField.materialDescription
                          ? getArrowDirection(sort.asc)
                          : undefined
                      }
                      arrowPosition="right"
                      onClick={() =>
                        onSort(DeliveryReportSortField.materialDescription)
                      }
                    >
                      {intl.formatMessage({ defaultMessage: "สินค้า" })}
                    </TableCellHeader>
                    <TableCellHeader
                      className={classes.col7}
                      direction={
                        sort.sort === DeliveryReportSortField.quantity
                          ? getArrowDirection(sort.asc)
                          : undefined
                      }
                      arrowPosition="right"
                      onClick={() => onSort(DeliveryReportSortField.quantity)}
                    >
                      {intl.formatMessage({ defaultMessage: "จำนวน" })}
                    </TableCellHeader>
                    <TableCellHeader className={classes.col8}>
                      <MyTypography variant={"body3"}>
                        {intl.formatMessage({ defaultMessage: "หน่วย" })}
                      </MyTypography>
                    </TableCellHeader>
                    <TableCellHeader
                      className={classes.col7}
                      textAlign={"center"}
                      direction={
                        sort.sort === DeliveryReportSortField.deliveryWeight
                          ? getArrowDirection(sort.asc)
                          : undefined
                      }
                      arrowPosition="right"
                      onClick={() =>
                        onSort(DeliveryReportSortField.deliveryWeight)
                      }
                    >
                      {intl.formatMessage({ defaultMessage: "น้ำหนัก" })}
                    </TableCellHeader>
                    <TableCellHeader className={classes.col8}>
                      <MyTypography variant={"body3"}>
                        {intl.formatMessage({ defaultMessage: "หน่วย" })}
                      </MyTypography>
                    </TableCellHeader>
                    <TableCellHeader
                      className={classes.col9}
                      direction={
                        sort.sort === DeliveryReportSortField.shipToName
                          ? getArrowDirection(sort.asc)
                          : undefined
                      }
                      arrowPosition="right"
                      onClick={() => onSort(DeliveryReportSortField.shipToName)}
                    >
                      {intl.formatMessage({ defaultMessage: "สถานที่ส่ง" })}
                    </TableCellHeader>
                    <TableCellHeader
                      className={classes.col10}
                      direction={
                        sort.sort === DeliveryReportSortField.giDate
                          ? getArrowDirection(sort.asc)
                          : undefined
                      }
                      arrowPosition="right"
                      onClick={() => onSort(DeliveryReportSortField.giDate)}
                    >
                      {intl.formatMessage({ defaultMessage: "วันเวลาตัดจ่าย" })}
                    </TableCellHeader>
                    <TableCellHeader
                      className={classes.col11}
                      direction={
                        sort.sort === DeliveryReportSortField.carRegistrationNo
                          ? getArrowDirection(sort.asc)
                          : undefined
                      }
                      arrowPosition="right"
                      onClick={() =>
                        onSort(DeliveryReportSortField.carRegistrationNo)
                      }
                    >
                      {intl.formatMessage({ defaultMessage: "ทะเบียนรถ" })}
                    </TableCellHeader>
                    <TableCellHeader
                      className={classes.col12}
                      direction={
                        sort.sort ===
                        DeliveryReportSortField.departurePlacePositions
                          ? getArrowDirection(sort.asc)
                          : undefined
                      }
                      arrowPosition="right"
                      onClick={() =>
                        onSort(DeliveryReportSortField.departurePlacePositions)
                      }
                    >
                      {intl.formatMessage({ defaultMessage: "ต้นทาง" })}
                    </TableCellHeader>
                    <TableCellHeader
                      className={classes.col13}
                      direction={
                        sort.sort === DeliveryReportSortField.estimateDateTime
                          ? getArrowDirection(sort.asc)
                          : undefined
                      }
                      arrowPosition="right"
                      onClick={() =>
                        onSort(DeliveryReportSortField.estimateDateTime)
                      }
                    >
                      {intl.formatMessage({
                        defaultMessage: "กำหนดส่งถึงลูกค้า"
                      })}
                    </TableCellHeader>
                    <TableCellHeader
                      className={classes.col14}
                      direction={
                        sort.sort === DeliveryReportSortField.transportStatus
                          ? getArrowDirection(sort.asc)
                          : undefined
                      }
                      arrowPosition="right"
                      onClick={() =>
                        onSort(DeliveryReportSortField.transportStatus)
                      }
                    >
                      {intl.formatMessage({ defaultMessage: "สถานะรถ" })}
                    </TableCellHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {renderCollection(currentData, (item, index) => {
                    const orderTrackingOutput = orderTrackingOutputList.find(
                      input => input.dnNo === item.dnNo
                    );
                    const enableTrackingIcon =
                      orderTrackingOutput?.statusId === "4" ||
                      orderTrackingOutput.statusId === "5";
                    return (
                      <TableRow key={`${item.itemNo}${index}`}>
                        <TableCell
                          className={classNames(
                            classes.col1,
                            classes.freeze1,
                            classes.zIndex1
                          )}
                        >
                          <SVG
                            src={truckIcon}
                            className={
                              enableTrackingIcon ? "" : classes.disabled
                            }
                            width={25}
                            height={25}
                            onClick={() => handleGpsTracking(item)}
                          />
                        </TableCell>
                        <TableCell
                          className={classNames(
                            classes.col2,
                            classes.freeze2,
                            classes.zIndex1
                          )}
                        >
                          <MyTypography variant="body3">
                            {orderTrackingOutput.dnNo}
                          </MyTypography>
                        </TableCell>
                        <TableCell
                          className={classNames(
                            classes.col20,
                            classes.freeze3,
                            classes.zIndex1
                          )}
                          onClick={() => handleDpLink(item, "billingNO")}
                        >
                          <MyTypography
                            variant="body3"
                            className={classes.dpLink}
                          >
                            {item?.billNo || "-"}
                          </MyTypography>
                        </TableCell>
                        <TableCell>
                          <MyTypography variant="body3">
                            {item.poNo}
                          </MyTypography>
                        </TableCell>
                        <TableCell>
                          <MyTypography variant="body3">
                            {item.soNo}
                          </MyTypography>
                        </TableCell>
                        <TableCell>
                          <MyTypography variant="body3">
                            {item.itemNo}
                          </MyTypography>
                        </TableCell>
                        <TableCell>
                          <MyTypography variant="body3">
                            {matNumberFilter(item) || "-"}
                          </MyTypography>
                        </TableCell>
                        <TableCell>
                          <MyTypography variant="body3">
                            {item.materialDescription}
                          </MyTypography>
                        </TableCell>
                        <TableCell className={classes.noBorderRight}>
                          <MyTypography variant="body3">
                            <FormattedNumber
                              value={item.deliveryQty}
                              {...(Number.isInteger(item?.deliveryQty)
                                ? {}
                                : {
                                    minimumFractionDigits: 3,
                                    maximumFractionDigits: 3
                                  })}
                            />
                          </MyTypography>
                        </TableCell>
                        <TableCell>
                          <MyTypography variant="body3">
                            {item.saleUnit}
                          </MyTypography>
                        </TableCell>
                        <TableCell className={classes.noBorderRight}>
                          <MyTypography variant="body3">
                            {item.deliveryWeight}
                          </MyTypography>
                        </TableCell>
                        <TableCell>
                          <MyTypography variant="body3">
                            {item.weightUnitTon}
                          </MyTypography>
                        </TableCell>
                        <TableCell>
                          <MyTypography variant="body3">
                            {orderTrackingOutput.destinationName || "-"}
                          </MyTypography>
                        </TableCell>
                        <TableCell>
                          <MyTypography variant="body3">
                            {orderTrackingOutput.cutOffDate
                              ? orderTrackingOutput.giTime &&
                                orderTrackingOutput.giTime !== "00:00:00"
                                ? moment(
                                    `${orderTrackingOutput.cutOffDate} ${orderTrackingOutput.giTime}`,
                                    "DD/MM/YYYY HH:mm:ss"
                                  ).format("DD / MM / YYYY HH:mm:ss")
                                : moment(
                                    orderTrackingOutput.cutOffDate,
                                    "DD/MM/YYYY"
                                  ).format("DD / MM / YYYY")
                              : "-"}
                          </MyTypography>
                        </TableCell>
                        <TableCell>
                          <MyTypography variant="body3">
                            {orderTrackingOutput.truckNo || "-"}
                          </MyTypography>
                        </TableCell>
                        <TableCell>
                          <MyTypography variant="body3">
                            {orderTrackingOutput.originName || "-"}
                          </MyTypography>
                        </TableCell>
                        <TableCell>
                          <MyTypography variant="body3">
                            {orderTrackingOutput.planDelivery
                              ? moment(
                                  orderTrackingOutput.planDelivery,
                                  [
                                    "DD-MM-YYYY HH:mm:ss",
                                    "YYYY-MM-DD HH:mm:ss",
                                    "YYYY-MM-DD"
                                  ],
                                  true
                                )
                                  .format("DD / MM / YYYY HH:mm:ss")
                                  .replace("00:00:00", "")
                              : "-"}
                          </MyTypography>
                        </TableCell>
                        <TableCell>
                          <MyTypography variant="body3">
                            {item.lmsStatus || "-"}
                          </MyTypography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              <StickyHorizontalScrollBar container={containerRef} />
            </TableContainer>
          </div>
          <Box className={classes.paginateFooter}>
            <TablePaginationCustom
              hasNextPage={pageInfo && !disabled ? pageInfo.hasNextPage : false}
              hasPreviousPage={
                pageInfo && !disabled ? pageInfo.hasPreviousPage : false
              }
              rowNumberSet={BIG_ROW_NUMBER_SET}
              onNextPage={onNextPage}
              onPreviousPage={onPreviousPage}
              totalCount={totalCount}
              selectColor="#007E92"
              settings={settings}
              onUpdateListSettings={onUpdateListSettings}
              isBackFistPage
              isNextLastPage
              latestPageItemNumber={latestPageItemNumber}
              onNextLastPage={onNextLastPage}
              onPreviousFirstPage={onPreviousFirstPage}
              disabled={disabled}
            />
          </Box>
          {orderTrackingOutput?.dnNo ? (
            <>
              <ModalGpsTracking
                dataItem={dataItem}
                open={openGpsTracking}
                onClose={() => setOpenGpsTracking(false)}
                responseItem={responseItem}
              />
              <ModalDp
                // orderTrackingOutput={orderTrackingOutput}
                dataItem={dataItemDpModal}
                open={openDp}
                onClose={() => setOpenDp(false)}
                modalInfomation={modalDpDetail}
              />
            </>
          ) : (
            ""
          )}
          <DialogProcessing
            open={loadingGpsTracking}
            description={"ระบบกำลังประมวลผล กรุณารอสักครู่"}
          />
        </>
      ) : null}
    </>
  );
};

export default DeliveryReportTableCustomer;
